import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMailboxItem } from "../../integrations/email/hooks/useMailboxItem";
import { useMailboxSnapshot } from "../../integrations/email/hooks/useMailboxSnapshot";
import { useChat } from "../../chat/hooks/useChat";
import { useNbaActions } from "../../integrations/engage/hooks/useNbaActions";
import { useUserProfile } from "../hooks/useUserProfile";
import { usePinnedModules } from "../../skills/hooks/usePinnedModules";
import { useAuthState } from "../../auth/AuthStateProvider";
import {
  NotificationsBell,
  NotificationToaster,
  APPROVAL_CHANGED_SIGNAL,
  isApprovalNotification,
  isHubConnected,
  subscribeToSignals,
  toSkillMessage,
  useNotifications,
  useNotificationToasts,
  type Notification,
  type SkillMessage,
} from "../../notifications";
import { DOCUMENT_CONTEXT_CHAR_CAP } from "../../chat/services/api";
import type { Channel } from "../host";
import { SettingsPage } from "../../settings/SettingsPage";
import { useTheme } from "../providers/ThemeProvider";
import { logEvent } from "../../telemetry";
import { useUserModulePrefs } from "../userSkillPrefs";
import { useWordDocument } from "../../integrations/document";
import { usePptTextSelection } from "../../integrations/powerpoint/hooks/usePptTextSelection";
import { ALL_MODULES } from "../../skills/registry";
import { findFrontendTileForBackendModule } from "../../skills/backendModuleTile";
import { useOptionalAppConfig } from "../../services/studio/AppConfigProvider";
import { fetchOutlookIntegrationSettings, StudioBadResponseError } from "../../services/studio/api";
import { setExternalRecipientWarnEnabled, setOutlookDisclaimerSettings } from "../../../integrations/tools/outlook/handlers";
import { CONNECTOR_PANELS } from "../../integrations/connectorRegistry";
import type { OutlookIntegrationSettings } from "../../services/studio/types";
import type { TabDefinition, TabId } from "../types";
import { Header } from "./Header";
import { PinHint } from "./PinHint";
import { ProvenanceFooter } from "./ProvenanceFooter";
import { UserProfileBar } from "./UserProfileBar";
import { ChannelUnavailableNotice, type ChannelBlockReason } from "./ChannelUnavailableNotice";
import { useWordUndoShortcut } from "../../integrations/tools/word/useWordUndoShortcut";
import { AccountNotFoundNotice } from "./AccountNotFoundNotice";
import { ConnectionErrorNotice } from "./ConnectionErrorNotice";
import { AccessBlockedNotice, type AccountBlockReason } from "./AccessBlockedNotice";
import { SettingsLoadingNotice } from "./SettingsLoadingNotice";
import { reloadAddin } from "../utils/reloadAddin";
import { deriveConnectionState } from "../utils/connectionState";
import { TabBar } from "./TabBar";
import { ChatTabPanel } from "../../chat/components/ChatTabPanel";
import { subscribeChatSheet } from "../../integrations/deckorate/features/action-chips/services/chatSheetBridge";
import { ActionsTabPanel, pendingActionCount } from "../../integrations/engage/components/ActionsTabPanel";
import { useActivities } from "../../approvals/hooks/useActivities";
import { ModulesTabPanel } from "../../skills/components/ModulesTabPanel";
import { EmailContextBar } from "../../integrations/email/components/EmailContextBar";
import { EmailModule } from "../../integrations/email/components/EmailModule";
import { CalendarModule } from "../../integrations/calendar/components/CalendarModule";
import { FilesModule } from "../../integrations/files/components/FilesModule";
import { EngageModule } from "../../integrations/engage/components/EngageModule";
import { DocuParserModule } from "../../integrations/docuparser/components/DocuParserModule";
import { MyQueueModule } from "../../integrations/myqueue/components/MyQueueModule";
import { TranscribeModule } from "../../integrations/transcribe/components/TranscribeModule";
import { KnowledgeModule } from "../../integrations/knowledge/components/KnowledgeModule";
import { ReviewModule } from "../../integrations/review/components/ReviewModule";
import { DeckorateModule } from "../../integrations/deckorate/components/DeckorateModule";
import { subscribeDeckorateNav } from "../../../integrations/tools/deckorate";
import { consumePendingInAppNav, requestInAppNav, resolveInAppTab, subscribeInAppNav } from "../inAppLinks";
import { openDocumentRecord, openEmailRecord } from "../openInAppRecord";
import { useOpenMessage } from "../../integrations/email/hooks/useOpenMessage";
import { toEwsId } from "../../../integrations/tools/outlook/appointment";
import { emitToast } from "../../../integrations/tools/outlook/toast-bridge";
import * as codeModeStore from "../../integrations/deckorate/codeMode/store";
import { CODE_MODE_FOCUS_CATCHER_ID } from "../../integrations/deckorate/codeMode/focusCatcher";
// The set of Deckorate screens an action chip can route to — also exactly the
// screens DeckorateModule accepts as an entry view.
import type { ChipTargetView as DeckorateEntryView } from "../../integrations/deckorate/features/action-chips/chipTargets";
import { TelemetryErrorBoundary } from "./TelemetryErrorBoundary";
import { ConnectorModuleShell } from "./ConnectorModuleShell";
import { getSkillCommandsForHost } from "../../chat/slashCommands/registry";
import { CommandPalette } from "../../integrations/deckorate/features/command-palette/components/CommandPalette";
import type { DeckorateCommand } from "../../integrations/deckorate/types";
import {
  focusComposerSoon,
  useAllActionsPalette,
  type PaletteHandlers,
} from "../../integrations/powerpoint/features/command-palette/hooks/useAllActionsPalette";
import { requestDeckorateCommand } from "../../integrations/powerpoint/features/command-palette/services/allActionsBridge";
import { ChatIcon, BoltIcon } from "./icons";
import { AI_NOTICE_COPY, AiNoticeCard, AiNoticeSheet, useAiNotice } from "../../aiNotice";
import styles from "./TaskPane.module.css";

interface TaskPaneProps {
  readonly host: typeof Office.HostType[keyof typeof Office.HostType] | null;
}

function getHostLabel(host: TaskPaneProps["host"]): string | null {
  if (host === Office.HostType.Outlook) return "Outlook";
  if (host === Office.HostType.Word) return "Word";
  if (host === Office.HostType.PowerPoint) return "PowerPoint";
  return null;
}

function getChatHost(host: TaskPaneProps["host"]): Channel | null {
  if (host === Office.HostType.Outlook) return "outlook";
  if (host === Office.HostType.Word) return "word";
  if (host === Office.HostType.PowerPoint) return "powerpoint";
  return null;
}

/**
 * The two AI-connected greetings of Word's ST-02: a document with content is named
 * with its word count, an empty one gets the documented "start writing" copy. The
 * spec's third state (AI not connected) is a connection fault, so it comes from
 * deriveConnectionState instead and overrides this.
 *
 * The slot renders as plain text, so the spec figure's bulleted capability list
 * cannot be reproduced here; and "Save to OneDrive or share via Engage" is omitted
 * because those modules are workspace-configured and may not be present.
 */
function getHostHint(
  host: TaskPaneProps["host"],
  hasEmail: boolean,
  docName: string,
  docWordCount: number,
  docLoading: boolean,
): string {
  if (host === Office.HostType.Outlook && hasEmail) return "Ask me about this email, draft a reply, or get a summary.";
  if (host === Office.HostType.Word) {
    // The document context starts empty and fills in asynchronously, so claiming
    // "empty" before it has loaded would tell a user with an 8,000-word agreement
    // that their document is blank.
    if (docLoading) return "Ask me about this document, help with writing, or get a summary.";
    if (docWordCount === 0) {
      return "The document appears to be empty. Start writing and I can help you with content, formatting, and analysis.";
    }
    const words = docWordCount.toLocaleString();
    return docName
      ? `I can see "${docName}" (${words} words). Ask me about this document, help with writing, or get a summary.`
      : `I can see this document (${words} words). Ask me about it, help with writing, or get a summary.`;
  }
  if (host === Office.HostType.PowerPoint) return "Ask me about this presentation, help with content, or get suggestions.";
  return "Hi! I'm AiVA, your AI assistant.";
}

function getInputPlaceholder(host: TaskPaneProps["host"]): string {
  if (host === Office.HostType.Outlook) return "Ask AiVA about this email...";
  if (host === Office.HostType.Word) return "Ask AiVA about this document...";
  if (host === Office.HostType.PowerPoint) return "Ask AiVA about this presentation...";
  return "Ask AiVA...";
}

// Persistent warning while an oversized SELECTION would ride the turn — shown
// regardless of what is typed, since selecting the text is itself the user's
// deliberate signal (AIVA-7002/W52).
//
// NOTHING SELECTED IS NOT A CAP CASE, and must not warn. A write request on an
// unselected document does not work from the truncated '--- Document Content ---'
// block at all: the /write skill reads the document's structure
// (word_get_document_structure), picks the matching TOC entry, and re-reads the
// body around it via word_get_selection_context with ±30 paragraphs. The section
// it drafts against is fetched by that anchored read, so the 6,000-char cap on
// the injected block does not bound what /write can see. Telling the user
// "AiVA will read the first 6,000 characters. Please select the relevant text
// instead." therefore described a limit that does not apply and pushed them into
// a manual selection they do not need — on a long agreement the anchored read is
// the more accurate route, not the fallback.
function getSelectionCapNotice(selectionOverCap: boolean, wordHasSelection: boolean): string | null {
  if (!selectionOverCap) return null;
  if (wordHasSelection) {
    return `Large selection — AiVA will read the first ${DOCUMENT_CONTEXT_CHAR_CAP.toLocaleString()} characters and ignore the rest.`;
  }
  return null;
}

/**
 * Host-direct tiles that are also real Studio catalogue modules — enabled for
 * the workspace on the Studio Modules page (StudioModule.Enabled), assigned
 * per-persona (Persona → Modules) and routed per-channel (Module Routing) —
 * rather than always-on native surfaces. They render only when the
 * persona-scoped settings.modules list includes them, so disabling the module
 * in Studio (or un-assigning the persona / un-routing the channel) hides the
 * tile in the add-in on the next session refresh (AIVA-6249). "Review" joined
 * this set once it gained a Review catalogue module (seeded by
 * 20260818120000_CorrelateAddinModules); every add-in host tile is now gated.
 */
const PERSONA_GATED_HOST_TILE_IDS = new Set<string>([
  "knowledge",
  "docuparser",
  "calendar",
  "email",
  "files",
  "deckorate",
  "review",
]);

/** Tabs that are part of the pane itself, never a revocable module. */
const FIXED_TAB_IDS = new Set<string>(["chat", "actions", "approvals", "modules"]);

/** Copy fixed by the specification (Outlook §3.2 / Word §3). */
const MODULE_UNAVAILABLE_TOAST = "This module is not available in this context.";

export function TaskPane({ host }: Readonly<TaskPaneProps>) {
  const mailboxItem = useMailboxItem();
  const { graphToken, apiToken, idToken, signOut, refreshTokens, refreshIdToken, refreshGraphToken, ensureFreshToken } = useAuthState();
  // Backend calls (chat, NBA, NBA execute) require an API-audience token so
  // APIM's validate-jwt policy can verify them. Until the API app reg is
  // configured per-environment, fall back to the Graph token (the backend's
  // legacy unverified-token path still accepts it).
  const backendToken = apiToken ?? graphToken;
  // The notifications hub + inbox live on the .NET add-in API, which reads the
  // user's email from the token claims — the OIDC id token carries those.
  const notificationToken = idToken ?? apiToken ?? graphToken;
  const { darkMode, setDarkMode } = useTheme();

  const isWord = host === Office.HostType.Word;
  const chatHost = getChatHost(host);
  // Ctrl+Z pressed in the pane reaches the WebView, not Word, so a document
  // change looks un-undoable until the user clicks back into the document.
  // Forwarded to the host undo stack on Word desktop; a no-op everywhere else.
  useWordUndoShortcut(chatHost);

  const { doc, fullDoc, loading: docLoading, readFailed: docReadFailed, refresh: refreshDoc, hasSelection: wordHasSelection, selectionOverCap, resolveSendContext } = useWordDocument(isWord);
  // G-10 (AIVA-8913): keeps the latest action bar's Replace selection in step
  // with what is selected in PowerPoint now, not when the turn was sent.
  const pptTextShapeSelected = usePptTextSelection(host === Office.HostType.PowerPoint);
  // Use doc directly — it carries the selection-retention delay so the selection
  // text is preserved while the user types in the task pane (clicking the task pane
  // clears the Word selection in some Office hosts before the user submits). The
  // retention now applies only when the task pane just took focus, so a deliberate
  // deselect clears immediately and the selection guard can trust this (AIVA-7078).
  const wordDocContext = (() => {
    if (!isWord) return null;
    return doc;
  })();

  const appConfig = useOptionalAppConfig();
  const outlookDisclaimer = appConfig?.data?.outlookDisclaimer;
  const clientId = appConfig?.data?.client.id;

  // The backend gates the whole add-in on persona-channel access: when the
  // user's persona isn't routed to this Office host (or has no persona), it
  // returns HasChannelAccess === false. Only act on an explicit false once the
  // load has settled — while idle/loading/errored we keep the normal surface so
  // a transient backend hiccup doesn't lock a legitimately-entitled user out.
  const noChannelAccess =
    appConfig?.status === "ready" && appConfig.data?.hasChannelAccess === false;

  // AIVA-5721: the settings endpoint 404s when the signed-in account is not on
  // the Console Users page in Studio (user / client-domain / channel not found
  // → KeyNotFoundException → 404). That is a definitive "you're not set up"
  // state, not a transient hiccup, so block the whole surface with a clear
  // notice instead of leaving the add-in showing an ambiguous "Disconnected"
  // status dot and a chat that only fails once the user sends a message.
  const accountNotFound =
    appConfig?.status === "error" &&
    appConfig.error instanceof StudioBadResponseError &&
    appConfig.error.status === 404;

  // AIVA-5817: hold the chat surface back while the settings load is still in
  // flight so it can't flash before the channel-access gate settles (the gate
  // only engages once status === "ready"). Only when a config provider is
  // present — without one (e.g. standalone) we keep the normal surface.
  const settingsPending =
    appConfig != null && (appConfig.status === "idle" || appConfig.status === "loading");

  // A settings load that failed with anything other than the definitive 404
  // (network drop, 5xx, timeout) is potentially transient. Rather than the
  // ambiguous red "Disconnected" dot over a chat that only fails on send, block
  // the surface with a clear "couldn't connect" notice + Retry (re-runs load).
  const connectionError = appConfig?.status === "error" && !accountNotFound;

  // The AI axis needs BOTH Studio tiers: Conversational answers chat, Reasoning
  // runs the skill router and most skills, and they are independent connectors.
  // Absent config and an unsettled probe both default to connected, so neither
  // service is blamed before it has been checked.
  const aiConnected =
    (appConfig?.data?.aiProviderConnected ?? true) &&
    (appConfig?.data?.reasoningProviderConnected ?? true);
  const m365Connected = appConfig?.m365Connected ?? true;

  // Set by the per-request access guard (accessGuard) when a mid-session Studio
  // change is detected: the user was deactivated/archived/removed, their persona
  // / connections changed, or channel access was lost. Takes precedence over the
  // load-state gates so the surface reflects the latest Studio state.
  const accessBlock = appConfig?.accessBlock ?? null;

  // A channel-level block can come from the mid-session guard OR the load-time
  // hasChannelAccess flag; both resolve to the exact reason so the notice can
  // name the app and say whether the workspace channel is off or the persona
  // lacks access.
  const channelBlockReason: ChannelBlockReason | null = (() => {
    if (accessBlock === "channel_disabled" || accessBlock === "persona_no_channel") {
      return accessBlock;
    }
    if (!noChannelAccess) return null;
    return appConfig?.data?.accessDeniedReason === "channel_disabled"
      ? "channel_disabled"
      : "persona_no_channel";
  })();

  // Account/persona/catalogue block (deactivated, suspended, archived, removed,
  // persona/modules changed) → AccessBlockedNotice. Everything except the two
  // channel reasons, which ChannelUnavailableNotice handles above.
  const accountBlockReason: AccountBlockReason | null =
    accessBlock == null ||
    accessBlock === "channel_disabled" ||
    accessBlock === "persona_no_channel"
      ? null
      : accessBlock;

  const [outlookSettings, setOutlookSettings] = useState<OutlookIntegrationSettings | null>(null);

  // Read token + refresh through refs so a token rotation (e.g. the reactive
  // refresh fetchWithRefresh runs on a 401) does NOT re-trigger this effect.
  // Mirrors useActivities / useNotifications. The effect keys off whether a
  // token EXISTS (a boolean that flips once), not its value — so a persistent
  // 401 fails once instead of looping refresh → re-render → refetch forever.
  const backendTokenRef = useRef(backendToken);
  backendTokenRef.current = backendToken;
  const refreshRef = useRef(refreshTokens);
  refreshRef.current = refreshTokens;
  const hasBackendToken = Boolean(backendToken);

  useEffect(() => {
    if (host !== Office.HostType.Outlook || !clientId || !hasBackendToken) return;
    fetchOutlookIntegrationSettings(clientId.toString(), backendTokenRef.current ?? undefined, {
      refresh: refreshRef.current,
    })
      .then(setOutlookSettings)
      .catch(() => undefined);
  }, [host, clientId, hasBackendToken]);

  const disclaimerText = (() => {
    if (host !== Office.HostType.Outlook) return null;
    if (outlookSettings) {
      return outlookSettings.isDisclaimerEnabledForAiGeneratedText
        ? outlookSettings.disclaimerForAiGeneratedText
        : null;
    }
    return outlookDisclaimer?.isEnabled ? (outlookDisclaimer.text ?? null) : null;
  })();

  // Whether Deckorate is in the module list (persona grant + Settings > Modules).
  // Only then can a PowerPoint chat turn take the Deckorate route.
  const deckorateEnabledRef = useRef(false);
  const {
    messages, input, setInput, streaming, activeTool, activityLine, docReviewScope, chatEndRef,
    handleSend, handlePillClick, handlePillAmend, handleMultipartStep, handleSlideRewrite, handlePillBooked, handleApprovalDecision, runSlashCommand, sendPrompt, clearChat, stopStreaming, regenerate,
    attachedFiles, addAttachedFiles, removeAttachedFile, sessionUploadedFileCount, sessionUploadedFileBytes,
    selectedNameCandidateIds,
    threadIdRef, docReviewReadyRef,
    skillSession, openSkillMessage, returnToPreviousChat,
  } = useChat(backendToken, mailboxItem, wordDocContext, chatHost, disclaimerText, refreshTokens, {
    personaDisplayName: appConfig?.data?.user.persona?.name ?? null,
    // AIVA-6350: gate NL queries when the Studio AI provider is disconnected.
    // Same value the status dot reads, so the dot and the send gate agree.
    aiProviderConnected: aiConnected,
    // Gates the mailbox/calendar skills that declare requiresMicrosoft365, so a
    // send that cannot succeed is refused before any AI call.
    m365Connected,
    // AIVA-5861: pre-flight token refresh so a chat send never goes out with a
    // token we already know is expired.
    ensureFreshToken,
    // Re-reads the Word selection/document at send time so a turn never rides
    // on a selection the user already deselected (the watcher only hears about
    // deselects when Word actually fires DocumentSelectionChanged).
    resolveSendWordDoc: isWord ? resolveSendContext : null,
    // Read at send time: the module list is assembled further down, after this
    // hook, so the ref is kept current by an effect next to hostModules.
    isDeckorateEnabled: () => deckorateEnabledRef.current,
  });

  // No longer keyed to what is typed: the cap notice is a SELECTION warning, and
  // an unselected write request is served by the skill's own anchored read rather
  // than the capped context block (see getSelectionCapNotice).
  const selectionCapNotice = getSelectionCapNotice(selectionOverCap, wordHasSelection);

  const { nbaItems, nbaLoading, nbaError, toggleNbaStatus } =
    useNbaActions(backendToken, refreshTokens);
  // Owned here rather than in the Actions tab so its pending count can badge
  // the tab while another tab is open.
  // With the hub up, pushes keep the list current and the poll is only a backstop.
  const activitiesState = useActivities(backendToken, refreshTokens, isHubConnected);

  const { profile, loading: profileLoading } = useUserProfile(graphToken);
  // AIVA-6639: the token's UPN first — it is available before Studio config
  // loads — so the gate decision does not wait on (or flip after) that fetch.
  // AIVA-8663: the notice text, validity and on/off switch come from Studio via the
  // settings payload; the gate below already waits for settings (settingsPending).
  const aiNotice = useAiNotice(
    profile?.mail || appConfig?.data?.user.email || null,
    chatHost,
    appConfig?.data?.aiNotice,
  );
  const paneContentRef = useRef<HTMLDivElement | null>(null);
  // Figures for the Outlook boot briefing (ST-02). Only fetched in the one state
  // that quotes them — Outlook with both services up — so no other host or
  // connection state pays for the Graph round-trip.
  const mailboxSnapshot = useMailboxSnapshot(
    graphToken,
    host === Office.HostType.Outlook && aiConnected && m365Connected,
  );
  // User-level module disables (Settings > Modules, ST-04). Subscribed live so
  // a toggle flipped in the Settings overlay reshapes the launcher and tab bar
  // behind it without a reload.
  const { disabledModuleIds: userDisabledModuleIds } = useUserModulePrefs(chatHost);
  const { pinnedModuleIds, togglePin, maxPins, pinLimitReached, dismissPinLimit } = usePinnedModules(
    chatHost,
    userDisabledModuleIds,
  );

  useEffect(() => {
    if (host !== Office.HostType.Outlook) return;
    if (outlookSettings) {
      setOutlookDisclaimerSettings({
        enabled: outlookSettings.isDisclaimerEnabledForAiGeneratedText,
        text: outlookSettings.disclaimerForAiGeneratedText,
      });
    } else if (outlookDisclaimer) {
      setOutlookDisclaimerSettings({ enabled: outlookDisclaimer.isEnabled, text: outlookDisclaimer.text });
    }
  }, [outlookSettings, outlookDisclaimer, host]);

  // The Studio "External recipient warning" toggle, from the add-in settings
  // payload. Absent (older backend, settings not loaded yet) means on.
  const externalRecipientWarnEnabled = appConfig?.data?.externalRecipientWarnEnabled ?? true;
  useEffect(() => {
    if (host !== Office.HostType.Outlook) return;
    setExternalRecipientWarnEnabled(externalRecipientWarnEnabled);
  }, [externalRecipientWarnEnabled, host]);

  // Telemetry does not carry user email from the browser — Telemetry.Endpoints
  // populates the UserEmail column server-side from the bearer-token claims.
  // The Graph profile is still read for UI surfaces (avatar, greeting) below.

  // Tile rendering is driven by the backend modules list, not the static
  // ALL_MODULES catalogue. Two passes:
  //   1. Host-direct frontend tiles. Every tile maps to a Studio catalogue
  //      module (see PERSONA_GATED_HOST_TILE_IDS) and renders only when the
  //      persona-scoped backend modules list includes it, so a module disabled
  //      in Studio disappears here (AIVA-6249). If the endpoint hasn't returned
  //      (loading or error), render no tiles so a broken backend doesn't expose
  //      modules the user isn't entitled to.
  //   2. For each backend module with at least one connector, render a tile
  //      labelled with the backend module's name (e.g. "Workflow"), reusing
  //      the matching frontend tile's icon and routing so the click still
  //      lands on a real panel.
  //
  // setupRequired is true when the backend lists the module but the credentials
  // call returned no authConfig for any of its connectors — surfaced on the tile
  // so the user knows the module isn't usable until an admin wires the keys.
  const hostModulesWithSetup = useMemo(() => {
    const studioModules = appConfig?.data?.modules;
    if (!studioModules) return [];

    const seenTileIds = new Set<string>();
    const fromBackend = studioModules.flatMap((bm) => {
      if (bm.connectors.length === 0) return [];
      const frontendTile = findFrontendTileForBackendModule(bm);
      if (!frontendTile) return [];
      if (seenTileIds.has(frontendTile.id)) return [];
      seenTileIds.add(frontendTile.id);
      const hasCreds = bm.connectors.some((c) => Boolean(c.authConfig));
      // Label the tile with the backend module's own Studio name so the add-in
      // matches Studio (AIVA-6249). The tile taxonomy and the Studio catalogue
      // now share the same names — even the historically-aliased modules
      // (Document Management, Email Management) — so the backend name always
      // reads correctly here; the alias table only maps the name to the tile id.
      return [
        {
          module: { ...frontendTile, name: bm.name },
          setupRequired: !hasCreds,
        },
      ];
    });

    // Tile ids the persona's backend modules resolve to, independent of whether
    // the module ships a connector — a connectorless module (e.g. Knowledge
    // Base, DocuParser) still name-matches its frontend tile. Used to gate
    // persona-scoped host-direct tiles below so they only appear when the
    // persona/channel grant the module in Studio.
    const backendTileIds = new Set(
      studioModules
        .map((bm) => findFrontendTileForBackendModule(bm)?.id)
        .filter((id): id is string => id != null),
    );

    // Host-direct tiles render after the backend pass, with backend-bound tile
    // ids excluded so a backend module that matched the same frontend tile
    // (e.g. Document Management → ALL_MODULES.files) doesn't appear twice.
    // Persona-gated host tiles (see PERSONA_GATED_HOST_TILE_IDS) additionally
    // require the persona's backend modules to include them.
    const hostDirect = ALL_MODULES
      .filter((m) => !(m.id in CONNECTOR_PANELS))
      .filter((m) => !seenTileIds.has(m.id))
      .filter((m) => !PERSONA_GATED_HOST_TILE_IDS.has(m.id) || backendTileIds.has(m.id))
      .map((m) => ({ module: m, setupRequired: false }));

    // Last gate: the user's own Settings > Modules disables. Applied to the
    // assembled list so a disabled module is "removed from the launcher and
    // tab bar entirely" (ST-04 AC). It is also unpinned (AIVA-9247).
    // Chat and Actions are fixed tabs, not modules, so they are untouchable.
    return [...hostDirect, ...fromBackend].filter((e) => !userDisabledModuleIds.has(e.module.id));
  }, [appConfig?.data?.modules, userDisabledModuleIds]);
  const hostModules = useMemo(
    () => hostModulesWithSetup.map((e) => e.module),
    [hostModulesWithSetup],
  );
  // Deckorate is in the module list (persona grant + Settings > Modules). Drives
  // the chat route (via the ref useChat reads at send time) and the chip dock.
  const deckorateEnabled = useMemo(
    () => hostModules.some((m) => m.id === "deckorate"),
    [hostModules],
  );
  useEffect(() => {
    deckorateEnabledRef.current = deckorateEnabled;
  }, [deckorateEnabled]);
  const setupRequiredIds = useMemo(
    () =>
      new Set(
        hostModulesWithSetup.filter((e) => e.setupRequired).map((e) => e.module.id),
      ),
    [hostModulesWithSetup],
  );
  const [activeTab, setActiveTab] = useState<TabId>("chat");
  // Feature screen the Deckorate tab should open on, set when a chat action
  // chip routes to it (AIVA-6561). DeckorateModule clears it via
  // onInitialViewConsumed once applied, so an ordinary later visit to that tab
  // lands on the home grid instead of reopening the same panel.
  const [deckorateEntryView, setDeckorateEntryView] = useState<DeckorateEntryView | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] =
    useState<"skills" | "connections" | "account">("skills");

  // Telemetry-wrapped tab-change handler. Every actual tab change (not a
  // no-op re-select) emits a single `nav.panel.open` event with the closed-
  // enum panel name. logEvent stays outside the state updater because React
  // StrictMode double-invokes updaters in dev, which would double-emit.
  const changeTab = useCallback((next: TabId) => {
    if (activeTab !== next) {
      logEvent("nav.panel.open", "usage", { panelName: String(next) });
    }
    setActiveTab(next);
  }, [activeTab]);

  // Chat's PowerPoint action chips open a Deckorate feature, which means both
  // switching tab and telling the module where to land (AIVA-6561).
  const openDeckorateView = useCallback(
    (view: DeckorateEntryView) => {
      setDeckorateEntryView(view);
      changeTab("deckorate");
    },
    [changeTab],
  );

  // The reverse trip: an "All actions" command whose feature is an in-pane chat
  // sheet needs the chat tab foregrounded before ChatTabPanel's own subscriber
  // opens it. Only the tab switch belongs here — which sheet opens is the chat
  // panel's business (see chatSheetBridge.ts).
  useEffect(() => subscribeChatSheet(() => changeTab("chat")), [changeTab]);

  // A persona-gated host tile (Knowledge Base, DocuParser) can be revoked
  // mid-session when a persona reassignment or module-routing change removes
  // it — the module's own
  // settings re-validation refreshes appConfig, dropping it from hostModules
  // (AIVA-7091). If the user is sitting on such a tab when it's no longer
  // granted, send them back to the modules grid rather than leaving them on a
  // surface they can't access. Account/channel revocation is handled by the
  // full-screen blocking notice instead.
  //
  // Gate on a *ready* settings load: while settings are still loading the tile
  // is legitimately absent from hostModules, so acting then would spuriously
  // redirect a deep-link/first paint before the grant is even known.
  useEffect(() => {
    if (appConfig?.status !== "ready") return;
    if (!PERSONA_GATED_HOST_TILE_IDS.has(String(activeTab))) return;
    const stillGranted = hostModules.some((m) => m.id === activeTab);
    if (!stillGranted) changeTab("modules");
  }, [appConfig?.status, activeTab, hostModules, changeTab]);


  // A Deckorate chat pill (e.g. "Apply Brand") opens a panel that lives in the
  // Deckorate tab, not the chat pane — bring that tab forward. Which panel is
  // DeckorateModule's half of the same request; it reads the target as it
  // mounts here (see integrations/tools/deckorate/nav-bridge).
  useEffect(() => {
    return subscribeDeckorateNav(() => changeTab("deckorate"));
  }, [changeTab]);

  // An Actions-tab card's Navigate / Open buttons (aiva:// links from a skill's
  // Notify card; see shared/inAppLinks). An email or document record opens
  // outside the pane, so no tab changes. Anything else brings the tab forward;
  // a module record stays parked for that module to open once it mounts.
  // A module the user can't reach must not appear "by any route" (ST-06), so
  // it toasts instead — judged only once settings are ready, since until then
  // every gated module is legitimately absent from hostModules.
  const openMessage = useOpenMessage();
  useEffect(() => {
    return subscribeInAppNav((target) => {
      // A link names a module as its author chose it (a tab id or a Studio name
      // such as "Email Management"); resolve it to the add-in's tab id first.
      const tab = FIXED_TAB_IDS.has(target.tab) ? target.tab : resolveInAppTab(target);
      if (target.record && tab === "email") {
        consumePendingInAppNav(target.tab);
        void openEmailRecord(target.record, openMessage, toEwsId, graphToken, refreshGraphToken);
        return;
      }
      if (target.record && tab === "files") {
        consumePendingInAppNav(target.tab);
        void openDocumentRecord(target.record, graphToken, refreshGraphToken);
        return;
      }
      const reachable =
        FIXED_TAB_IDS.has(tab) ||
        appConfig?.status !== "ready" ||
        hostModules.some((m) => m.id === tab);
      if (!reachable) {
        consumePendingInAppNav(target.tab);
        emitToast({ title: MODULE_UNAVAILABLE_TOAST, intent: "error" });
        return;
      }
      if (target.record && tab !== target.tab) {
        // The module claims its record under its own tab id: re-park it there.
        // The re-request comes back through this listener already resolved.
        consumePendingInAppNav(target.tab);
        requestInAppNav({ ...target, tab });
        return;
      }
      changeTab(tab);
    });
  }, [changeTab, openMessage, graphToken, refreshGraphToken, appConfig?.status, hostModules]);

  // Code Mode's leader (Alt+Shift+K) is owned here, at the pane root, rather
  // than inside DeckorateModule — that module is only mounted while the
  // Deckorate tab is active, so a listener living there is unreachable from
  // Chat or Actions, which is exactly where the user is when they reach for
  // the leader. Arming also brings the Deckorate tab forward, because the
  // keydown handling that consumes the code letters, and the dispatch that
  // runs them, both live in that module.
  //
  // Capture phase on `document`, matching useCommandPalette's Ctrl+Shift+K:
  // a bubble-phase listener is the last to see a keydown and gets eaten by
  // anything upstream calling stopPropagation().
  useEffect(() => {
    function handleLeader(event: KeyboardEvent) {
      // TEMPORARY DEBUG (AIVA-7971) — remove before merging.
      // Fires only for keystrokes the WebView actually receives, so silence
      // here while pressing a key means the keystroke never reached the pane
      // at all (focus is on the slide, and Office is the only thing that can
      // deliver it).
      const target = event.target as HTMLElement | null;
      const active = document.activeElement as HTMLElement | null;
      console.log("[codemode] pane keydown", {
        code: event.code,
        key: event.key,
        alt: event.altKey,
        shift: event.shiftKey,
        ctrl: event.ctrlKey,
        meta: event.metaKey,
        target: target?.id || target?.tagName,
        activeElement: active?.id || active?.tagName,
      });
      if (event.code !== "KeyK") return;
      if (!event.altKey || !event.shiftKey || event.ctrlKey || event.metaKey) return;
      // AIVA-6639: the shortcut must not reach Code Mode past the AI notice sheet.
      if (paneContentRef.current?.hasAttribute("inert")) return;
      console.log("[codemode] LEADER matched in pane -> switching tab + arming");
      event.preventDefault();
      changeTab("deckorate");
      codeModeStore.arm();
    }
    document.addEventListener("keydown", handleLeader, { capture: true });
    return () => document.removeEventListener("keydown", handleLeader, { capture: true });
  }, [changeTab]);

  // Real-time notifications: live SignalR push + inbox catch-up. Opening one
  // follows its deep-link (e.g. an approval → the Actions tab, which loads
  // fresh on mount), so no extra refresh plumbing is needed.
  // Transient top-right toasts for live pushes and for items the inbox catch-up
  // finds were missed while offline; `pushToast` is fed to useNotifications
  // below as its per-notification callback. The bell/inbox stay the
  // durable source of truth — toasts are purely the ephemeral pop-up.
  const { toasts, push: pushToast, dismiss: dismissToast } = useNotificationToasts();
  // The Actions list refreshes on push rather than on a timer: an approval
  // notification means a gate parked, escalated or failed, so the list is
  // stale the moment it arrives. Other pushes (skill messages) leave it alone.
  // Jittered: one persona approval reaches every member's pane at once.
  const { refreshSoon: refreshActivitiesSoon } = activitiesState;
  // Another approver decided a card, or its timeout resolved it.
  useEffect(
    () =>
      subscribeToSignals((signal) => {
        if (signal.type === APPROVAL_CHANGED_SIGNAL) refreshActivitiesSoon();
      }),
    [refreshActivitiesSoon],
  );
  const handleLivePush = useCallback(
    (n: Notification) => {
      pushToast(n);
      if (isApprovalNotification(n)) refreshActivitiesSoon();
    },
    [pushToast, refreshActivitiesSoon],
  );
  // The notifications hub re-acquires a bearer on EVERY (re)connect, and it
  // keeps retrying (on backoff) while the backend hub is unreachable, so its
  // refresher must never eject the user when one attempt misses. The id-token
  // refresh matches the hub/inbox bearer audience (notificationToken is the id
  // token), and `foreground=false` marks this as a background caller, so a dead
  // session is not acted on here.
  const refreshNotificationToken = useCallback(() => refreshIdToken(false), [refreshIdToken]);
  const { notifications, unreadCount, connected, markAsRead, markAllAsRead } = useNotifications(
    notificationToken,
    refreshNotificationToken,
    handleLivePush,
  );
  // Anything pushed while the hub was down is gone, so a REconnect re-syncs the
  // list. The first connect is skipped: the list loaded on mount moments ago.
  // Jittered: a service-wide drop reconnects every pane together.
  const hubConnectedOnceRef = useRef(false);
  useEffect(() => {
    if (!connected) return;
    if (hubConnectedOnceRef.current) refreshActivitiesSoon();
    hubConnectedOnceRef.current = true;
  }, [connected, refreshActivitiesSoon]);
  // Skills' text messages, listed in the Actions tab; unread ones count as pending.
  const skillMessages = useMemo(
    () => notifications.map(toSkillMessage).filter((m): m is SkillMessage => m !== null),
    [notifications],
  );
  const pendingActions = pendingActionCount(activitiesState, nbaItems, skillMessages);
  // Opening a skill message (from the Actions tab, the bell or its toast) starts
  // the message's own chat session, where its quick replies are pills.
  const openSkillMessageSession = useCallback(
    (message: SkillMessage) => {
      if (!message.read) void markAsRead(message.notificationId);
      changeTab("chat");
      openSkillMessage(message);
    },
    [markAsRead, changeTab, openSkillMessage],
  );
  const openNotification = useCallback(
    (n: Notification) => {
      const skillMessage = toSkillMessage(n);
      if (skillMessage) {
        openSkillMessageSession(skillMessage);
      } else if (n.deepLink === "approvals") {
        // Approvals render inside the Actions list (AIVA-6131), so the
        // backend's "approvals" deep-link opens the Actions tab.
        changeTab("actions");
      } else if (n.deepLink) {
        changeTab(n.deepLink);
      }
    },
    [changeTab, openSkillMessageSession],
  );
  // Clicking a toast follows its deep-link, marks it read, and clears the toast.
  const openToast = useCallback(
    (n: Notification) => {
      openNotification(n);
      void markAsRead(n.id);
      dismissToast(n.id);
    },
    [openNotification, markAsRead, dismissToast],
  );

  const promptInChat = useCallback((prompt: string) => {
    setInput(prompt);
    changeTab("chat");
  }, [changeTab, setInput]);

  // Module tile click — emit `nav.module.select` in addition to the
  // `nav.panel.open` that `changeTab` already logs, so the two signals
  // can be separated in the dashboard (tile click vs tab switch).
  const handleOpenModule = useCallback((moduleId: TabId) => {
    logEvent("nav.module.select", "usage", { moduleId: String(moduleId) });
    changeTab(moduleId);
  }, [changeTab]);

  const handleBack = useCallback(() => changeTab("modules"), [changeTab]);
  const openSettings = useCallback(() => {
    setSettingsInitialTab("skills");
    setSettingsOpen(true);
  }, []);
  const openConnectionsSettings = useCallback(() => {
    setSettingsInitialTab("connections");
    setSettingsOpen(true);
  }, []);

  // ST-05 (AIVA-6805): the PowerPoint "All actions" palette — Ctrl/Cmd+Shift+K
  // (Deckorate's key, now answered on every tab) or Deckorate's home card. PowerPoint's own actions always; Deckorate's design
  // actions too when it is on. Every action reuses a handle this component
  // already owns.
  const paletteHandlers = useMemo<PaletteHandlers>(
    () => ({
      sendPrompt: (text) => {
        changeTab("chat");
        void sendPrompt(text);
      },
      prefill: (text) => {
        changeTab("chat");
        setInput(text);
        focusComposerSoon();
      },
      openActions: () => changeTab("actions"),
      newChat: () => {
        changeTab("chat");
        clearChat();
      },
      openSettings,
    }),
    [changeTab, sendPrompt, setInput, clearChat, openSettings],
  );
  // Design actions run inside DeckorateModule, mounted only on its tab.
  const runDeckorateCommand = useCallback(
    (command: DeckorateCommand) => {
      changeTab("deckorate");
      requestDeckorateCommand(command);
    },
    [changeTab],
  );
  const getPaletteSkills = useCallback(() => getSkillCommandsForHost(chatHost), [chatHost]);
  const isPaneBlocked = useCallback(() => paneContentRef.current?.hasAttribute("inert") ?? false, []);
  const palette = useAllActionsPalette({
    enabled: host === Office.HostType.PowerPoint,
    deckorateEnabled,
    getSkills: getPaletteSkills,
    isBlocked: isPaneBlocked,
    handlers: paletteHandlers,
    runDeckorateCommand,
  });

  const hostLabel = getHostLabel(host);
  // Prefer the Studio-curated name over the MSAL claim, matching UserProfileBar.
  const connection = deriveConnectionState({
    settingsStatus: appConfig?.status,
    aiConnected,
    m365Connected,
    displayName: appConfig?.data?.user.displayName ?? profile?.displayName ?? null,
    host: chatHost,
    mailbox: mailboxSnapshot,
    // fullDoc, not doc: `doc` swaps in the SELECTION's text and word count while
    // something is selected, and the greeting must describe the document. Held
    // back until the read settles so an 8,412-word file is never greeted as empty.
    document:
      isWord && !docLoading && !docReadFailed
        ? { name: fullDoc.name, wordCount: fullDoc.wordCount }
        : null,
    documentUnavailable: isWord && docReadFailed,
  });
  // deriveConnectionState returns null while settingsStatus is unsettled, and
  // for "no host" with everything connected — getHostHint covers both gaps,
  // which is why it keeps its own Word word-count branch.
  const greeting =
    connection.greeting ??
    getHostHint(host, !!mailboxItem.subject, doc.name, doc.wordCount, docLoading);
  const inputPlaceholder = getInputPlaceholder(host);

  // Any gate blocks the whole functional surface; the notice explains which.
  // Computed here (not as a nested ternary in JSX) so the render stays flat.
  const surfaceBlocked =
    accountBlockReason != null ||
    channelBlockReason != null ||
    accountNotFound ||
    connectionError;
  // The gate only guards the functional surface: a blocking notice or the
  // settings-loading state already shows no AI features to acknowledge.
  const aiNoticeGateVisible = aiNotice.gateOpen && !surfaceBlocked && !settingsPending;
  const aiNoticeSheetOpen = aiNoticeGateVisible || aiNotice.reviewOpen;
  // `inert` (not just a scrim) is what keeps Tab focus and clicks off the pane
  // behind the sheet. Set imperatively: React 18 does not know the attribute.
  // A layout effect so `inert` is lifted before the closing sheet's passive
  // cleanup returns focus to its opener inside the pane.
  useLayoutEffect(() => {
    paneContentRef.current?.toggleAttribute("inert", aiNoticeSheetOpen);
  }, [aiNoticeSheetOpen]);
  const blockingNotice = (() => {
    // Account-level revocation/change wins: it reflects the latest Studio state.
    // Definitive state with no Retry (see AccessBlockedNotice).
    if (accountBlockReason) return <AccessBlockedNotice reason={accountBlockReason} />;
    if (accountNotFound) return <AccountNotFoundNotice email={profile?.mail} />;
    // Connection lost is the only case with a Retry — it reloads the add-in so a
    // fresh boot re-signs-in and re-establishes the connection from scratch.
    if (connectionError) return <ConnectionErrorNotice onRetry={reloadAddin} />;
    // Channel unavailable, with exact copy naming the app + reason.
    if (channelBlockReason) return <ChannelUnavailableNotice hostLabel={hostLabel} reason={channelBlockReason} />;
    return null;
  })();

  const tabs: TabDefinition[] = [
    { id: "chat", label: "Chat", icon: ChatIcon },
    { id: "actions", label: "Actions", icon: BoltIcon, badge: pendingActions },
    ...pinnedModuleIds
      .map(id => {
        const mod = hostModules.find(m => m.id === id);
        return mod ? { id: mod.id, label: mod.name, icon: mod.icon } : null;
      })
      .filter((t): t is TabDefinition => t !== null),
  ];

  const activeModuleLabel =
    tabs.find((t) => t.id === activeTab)?.label ??
    ALL_MODULES.find((m) => m.id === activeTab)?.name ??
    "this area";
  return (
    <div className={styles.container}>
      <div ref={paneContentRef} className={styles.paneContent}>
      <NotificationToaster toasts={toasts} onOpen={openToast} onDismiss={dismissToast} />
      <Header
        actions={
          <NotificationsBell
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkRead={markAsRead}
            onMarkAllRead={markAllAsRead}
            onOpen={openNotification}
          />
        }
      />
      {host === Office.HostType.Outlook && <PinHint />}
      <UserProfileBar
        profile={profile}
        loading={profileLoading}
        personaName={appConfig?.data?.user.persona?.name ?? null}
        displayName={appConfig?.data?.user.displayName ?? null}
        connectionStatus={connection.dot}
        connectionTooltip={connection.tooltip}
        onSettingsClick={openSettings}
      />
      {settingsOpen && (
        <SettingsPage
          onBack={() => setSettingsOpen(false)}
          initialTab={settingsInitialTab}
          host={chatHost}
          userEmail={profile?.mail ?? ""}
          darkMode={darkMode}
          onToggleDarkMode={setDarkMode}
          onSignOut={() => {
            setSettingsOpen(false);
            signOut();
          }}
        />
      )}
      {palette.isOpen && (
        <CommandPalette
          catalog={palette.catalog}
          showCodes={palette.showCodes}
          onRunCommand={palette.run}
          onClose={palette.close}
        />
      )}
      {(() => {
        // The account is not registered in Studio (accountNotFound) or the
        // persona has no access to this channel (noChannelAccess) — block the
        // entire functional surface (no tabs, no chat) and explain why, rather
        // than leaving an ambiguous "Disconnected" status. The profile bar
        // above still shows who is signed in.
        if (surfaceBlocked) return !settingsOpen && blockingNotice;
        // Settings still loading — show a neutral loading state instead of the
        // chat surface so a disabled channel/persona doesn't flash the chat
        // before the access gate resolves (AIVA-5817).
        if (settingsPending) return !settingsOpen && <SettingsLoadingNotice />;
        return (
      <div className={settingsOpen ? styles.hiddenTabPane : styles.activeTabPane}>
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={changeTab}
        onMoreClick={() => changeTab("modules")}
        moreActive={activeTab === "modules"}
      />

      {/* Open-item banner — names the mail item AiVA holds in context so the
          user can confirm what "reply to this" will act on. Outside
          tabContent so it stays visible on every tab, and Outlook-only
          because the other hosts have no mailbox item. */}
      {host === Office.HostType.Outlook && (
        <EmailContextBar mailboxItem={mailboxItem} />
      )}

      <div className={styles.tabContent}>
        <div className={activeTab === "chat" ? styles.activeTabPane : styles.hiddenTabPane}>
          <ChatTabPanel
            messages={messages}
            streaming={streaming}
            activeTool={activeTool}
            activityLine={activityLine}
            docReviewScope={docReviewScope}
            chatEndRef={chatEndRef}
            greeting={greeting}
            inputPlaceholder={inputPlaceholder}
            input={input}
            onInputChange={setInput}
            onSubmit={handleSend}
            onPillClick={handlePillClick}
            selectedNameCandidateIds={selectedNameCandidateIds}
            onApprovalDecision={handleApprovalDecision}
            onMultipartStep={handleMultipartStep}
            onSlideRewrite={handleSlideRewrite}
            onPillAmend={handlePillAmend}
            onPillBooked={handlePillBooked}
            host={chatHost}
            onSlashCommand={runSlashCommand}
            apiToken={backendToken}
            refreshTokens={refreshTokens}
            hasWordSelection={wordHasSelection}
            pptTextShapeSelected={pptTextShapeSelected}
            hasOpenEmail={!!mailboxItem.subject}
            onNewChat={clearChat}
            onStop={stopStreaming}
            onRegenerate={regenerate}
            attachedFiles={attachedFiles}
            onAttachFiles={addAttachedFiles}
            onRemoveFile={removeAttachedFile}
            sessionFileCount={sessionUploadedFileCount}
            sessionFileBytes={sessionUploadedFileBytes}
            contextNotice={selectionCapNotice}
            onOpenDeckorateView={openDeckorateView}
            deckorateEnabled={deckorateEnabled}
            docReviewThreadId={threadIdRef.current}
            docReviewReady={docReviewReadyRef.current}
            skillSession={skillSession}
            onReturnToPreviousChat={() => void returnToPreviousChat()}
          />
        </div>

        <TelemetryErrorBoundary
          key={activeTab}
          boundary={activeTab}
          label={`${activeModuleLabel} could not be displayed. Try again, or switch to another tab.`}
        >
          {activeTab === "actions" && (
            <ActionsTabPanel
              nbaItems={nbaItems}
              nbaLoading={nbaLoading}
              nbaError={nbaError}
              onToggleNbaStatus={toggleNbaStatus}
              activities={activitiesState}
              messages={skillMessages}
              onOpenMessage={openSkillMessageSession}
            />
          )}

          {activeTab === "modules" && (
            <ModulesTabPanel
              modules={hostModules}
              pinnedModuleIds={pinnedModuleIds}
              onTogglePin={togglePin}
              onOpenModule={handleOpenModule}
              setupRequiredIds={setupRequiredIds}
              maxPins={maxPins}
              pinLimitReached={pinLimitReached}
              onDismissPinLimit={dismissPinLimit}
              footerContent={
                aiNotice.policy.isEnabled && (
                  <AiNoticeCard
                    acknowledgedAt={aiNotice.acknowledgement?.acknowledgedAt ?? null}
                    onOpen={aiNotice.openReview}
                    version={aiNotice.policy.version}
                  />
                )
              }
            />
          )}

          {activeTab === "email" && (
            <EmailModule graphToken={graphToken} onPromptInChat={promptInChat} onBack={handleBack} />
          )}
          {activeTab === "calendar" && <CalendarModule graphToken={graphToken} onBack={handleBack} />}
          {activeTab === "files" && <FilesModule graphToken={graphToken} onBack={handleBack} />}
          {activeTab === "engage" && (
            <ConnectorModuleShell
              moduleId="engage"
              onBack={handleBack}
              onOpenSettings={openConnectionsSettings}
              userEmail={profile?.mail ?? undefined}
              fallback={EngageModule}
            />
          )}
          {/* DocuParser is host-direct — no connector/credentials to resolve —
               but persona-gated like Knowledge Base (see PERSONA_GATED_HOST_TILE_IDS):
               the tile only appears when the persona's backend modules include it.
               Connector version below for revert. */}
          {activeTab === "docuparser" && (
            <DocuParserModule
              onBack={handleBack}
              onOpenSettings={openConnectionsSettings}
            />
          )}
          {/* Revert path — connector-based DocuParser routing:
          {activeTab === "docuparser" && (
            <ConnectorModuleShell
              moduleId="docuparser"
              onBack={handleBack}
              onOpenSettings={openConnectionsSettings}
              fallback={DocuParserModule}
            />
          )}
          */}
          {activeTab === "myqueue" && (
            <ConnectorModuleShell
              moduleId="myqueue"
              onBack={handleBack}
              onOpenSettings={openConnectionsSettings}
              fallback={MyQueueModule}
            />
          )}
          {activeTab === "transcribe" && (
            <ConnectorModuleShell
              moduleId="transcribe"
              onBack={handleBack}
              onOpenSettings={openConnectionsSettings}
              fallback={TranscribeModule}
            />
          )}
          {activeTab === "knowledge" && <KnowledgeModule graphToken={graphToken} onBack={handleBack} />}
          {activeTab === "review" && (
            <ReviewModule
              onBack={handleBack}
              host={host}
              doc={doc}
              docLoading={docLoading}
              onRefreshDoc={refreshDoc}
              onPromptInChat={promptInChat}
            />
          )}
          {/* AIVA-6071: Deckorate now host-direct; connector version below for revert. */}
          {activeTab === "deckorate" && (
            <DeckorateModule
              onBack={handleBack}
              onOpenSettings={openConnectionsSettings}
              initialView={deckorateEntryView}
              onInitialViewConsumed={() => setDeckorateEntryView(null)}
            />
          )}
          {/* Revert path (AIVA-6071) — connector-based Deckorate routing:
          {activeTab === "deckorate" && (
            <ConnectorModuleShell
              moduleId="deckorate"
              onBack={handleBack}
              onOpenSettings={openConnectionsSettings}
              fallback={DeckorateModule}
            />
          )}
          */}
        </TelemetryErrorBoundary>
      </div>
      </div>
        );
      })()}
    
      {/* Code Mode's focus target for the slide-side leader. Visually hidden
          but deliberately NOT display/visibility hidden — those cannot take
          focus at all. See codeMode/focusCatcher.ts for why this is a real
          input rather than a tabIndex container, and why it never receives
          text despite being focused. */}
      <input
        id={CODE_MODE_FOCUS_CATCHER_ID}
        type="text"
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        style={{ position: "fixed", top: 0, left: 0, width: 1, height: 1, opacity: 0, border: 0, padding: 0 }}
      />
      {/* AIVA-8663: a notice switched off in Studio has no footer link either. */}
      <ProvenanceFooter
        onAiNoticeClick={aiNotice.policy.isEnabled ? aiNotice.openReview : undefined}
        aiNoticeLabel={AI_NOTICE_COPY.footerLink}
      />
      </div>
      {aiNoticeGateVisible && (
        <AiNoticeSheet mode="gate" policy={aiNotice.policy} onAcknowledge={aiNotice.acknowledge} />
      )}
      {!aiNoticeGateVisible && aiNotice.reviewOpen && (
        <AiNoticeSheet
          mode="review"
          policy={aiNotice.policy}
          acknowledgement={aiNotice.acknowledgement}
          onClose={aiNotice.closeReview}
        />
      )}
    </div>
  );
}
